import { useEffect, useCallback, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Autocomplete,
  Chip,
  Alert,
  AlertTitle,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InfoOutlined as InfoOutlinedIcon,
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useSecurityStore, type SensitivityCategory } from '../stores/securityStore';
import { useFilterStore } from '../stores/filterStore';
import { useExclusionsStore } from '../stores/exclusionsStore';
import { generateSecurityAssessment, acceptSecurityRisk, analyzeDocumentCSV } from '../api/dashboard';
import { formatOrgDisplayName } from '../utils/display';
import GenerateSecurityReportDialog from '../components/GenerateSecurityReportDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import type {
  HealthStatus,
  RiskSeverity,
  CategoryScore,
  SecurityRisk,
  RemediationAction,
  SensitivityBreakdown,
  DocumentAnalysisResponse,
} from '../types';

// Health status configuration
const HEALTH_STATUS_CONFIG: Record<
  HealthStatus,
  { color: string; icon: React.ReactNode; label: string; description: string }
> = {
  healthy: {
    color: '#10b981',
    icon: <CheckCircleIcon sx={{ color: '#10b981', fontSize: 48 }} />,
    label: 'Healthy',
    description: 'Strong security posture with minimal concerns',
  },
  moderate: {
    color: '#f59e0b',
    icon: <WarningIcon sx={{ color: '#f59e0b', fontSize: 48 }} />,
    label: 'Moderate',
    description: 'Acceptable security with some areas for improvement',
  },
  at_risk: {
    color: '#f97316',
    icon: <ErrorIcon sx={{ color: '#f97316', fontSize: 48 }} />,
    label: 'At Risk',
    description: 'Significant security concerns requiring attention',
  },
  critical: {
    color: '#ef4444',
    icon: <ErrorIcon sx={{ color: '#ef4444', fontSize: 48 }} />,
    label: 'Critical',
    description: 'Severe security risks requiring immediate action',
  },
};

// Risk severity configuration
const RISK_SEVERITY_CONFIG: Record<RiskSeverity, { color: string; label: string }> = {
  critical: { color: '#ef4444', label: 'Critical' },
  high: { color: '#f97316', label: 'High' },
  medium: { color: '#f59e0b', label: 'Medium' },
  low: { color: '#3b82f6', label: 'Low' },
};

// Category display names (currently unused but kept for future reference)
// const CATEGORY_NAMES: Record<SecurityCategory, string> = {
//   access_control: 'Access Control',
//   authentication: 'Authentication',
//   data_protection: 'Data Protection',
//   integration_security: 'Integration Security',
//   monitoring_response: 'Monitoring & Response',
//   compliance: 'Compliance',
// };

// Category descriptions for tooltips
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  'Access Control':
    'Evaluates user account management, permission controls, and access governance. Assesses privileged user management and permission escalation risks. Strong access control ensures users only have the minimum necessary permissions and follow the principle of least privilege.',
  Authentication:
    'Measures the strength of user authentication mechanisms and login security. Tracks failed login attempts, off-hours access patterns, and unusual login locations. Robust authentication prevents unauthorized access through strong password policies, multi-factor authentication, and anomaly detection.',
  'Data Protection':
    'Analyzes data handling practices and information security controls. Monitors bulk data operations, external data sharing, and sensitive data exposure. Effective data protection ensures sensitive information is encrypted, access is logged, and data transfers follow security policies.',
  'Integration Security':
    'Reviews security of API integrations, third-party services, and external connections. Examines API key management, webhook security, and integration authentication. Secure integrations prevent unauthorized access through properly configured API credentials and secure communication channels.',
  'Monitoring & Response':
    'Assesses security monitoring capabilities and incident response readiness. Evaluates logging practices, alert configurations, and suspicious activity detection. Strong monitoring enables rapid identification and response to security incidents before they cause significant damage.',
  Compliance:
    'Measures adherence to security policies, regulatory requirements, and industry standards. Reviews audit trail completeness, policy documentation, and compliance reporting. Good compliance demonstrates commitment to security best practices and regulatory obligations like GDPR, SOC 2, or ISO 27001.',
};

// Period options
const PERIOD_OPTIONS = [
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '60 Days', value: 60 },
  { label: '90 Days', value: 90 },
];

export default function SecurityAssessment() {
  const {
    assessment,
    isLoading,
    error,
    selectedOrgId,
    periodDays,
    includeSensitiveAnalysis,
    selectedSensitivityCategories,
    availableOrgs,
    setAssessment,
    setLoading,
    setError,
    setSelectedOrgId,
    setPeriodDays,
    setIncludeSensitiveAnalysis: _setIncludeSensitiveAnalysis,
    setSelectedSensitivityCategories,
    setAvailableOrgs,
  } = useSecurityStore();



  const { availableOptions, isOptionsLoaded } = useFilterStore();
  const { excludedOrgIds, excludedUserIds } = useExclusionsStore();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvAnalysis, setCsvAnalysis] = useState<DocumentAnalysisResponse | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInitializedRef = useRef(false);

  // Initial load: use orgs from filterStore (loaded by AuthGuard)
  useEffect(() => {
    if (isInitializedRef.current) return;

    const initialize = async () => {
      // Use orgs from filterStore if available (loaded by AuthGuard)
      if (isOptionsLoaded && availableOrgs.length === 0 && availableOptions?.orgIds) {
        setAvailableOrgs(availableOptions.orgIds);
      }
      isInitializedRef.current = true;
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoaded]);

  // Fetch security assessment
  const fetchAssessment = useCallback(async () => {
    if (!selectedOrgId) {
      return;
    }

    // Fix: Clear error first, then set loading
    // setError sets isLoading: false by default, so it must happen before setLoading(true)
    setError(null);
    setLoading(true);

    try {


      const response = await generateSecurityAssessment({
        orgId: selectedOrgId,
        periodDays,
        includeSensitiveAnalysis,
        sensitivityCategories: selectedSensitivityCategories,
        excludeOrgIds: excludedOrgIds,
        excludeUserIds: excludedUserIds,
      });
      setAssessment(response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate security assessment';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, periodDays, includeSensitiveAnalysis, selectedSensitivityCategories, excludedOrgIds, excludedUserIds, setLoading, setError, setAssessment]);

  const handleGenerateAssessment = () => {
    fetchAssessment();
  };

  const [acceptRiskDialogOpen, setAcceptRiskDialogOpen] = useState(false);
  const [selectedRiskToAccept, setSelectedRiskToAccept] = useState<SecurityRisk | null>(null);
  const [acceptReason, setAcceptReason] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);

  const handleOpenAcceptDialog = (risk: SecurityRisk) => {
    setSelectedRiskToAccept(risk);
    setAcceptReason('');
    setAcceptRiskDialogOpen(true);
  };

  const handleConfirmAcceptRisk = async () => {
    if (!selectedRiskToAccept || !selectedOrgId) return;

    setIsAccepting(true);
    try {
      await acceptSecurityRisk({
        orgId: selectedOrgId,
        riskId: selectedRiskToAccept.riskId,
        riskTitle: selectedRiskToAccept.title,
        acceptedBy: 'current_user', // TODO: Get actual user ID
        reason: acceptReason,
      });
      setAcceptRiskDialogOpen(false);
      // Refresh assessment to remove the accepted risk
      fetchAssessment();
    } catch (err) {
      console.error('Failed to accept risk:', err);
      // Optional: Show error toast
    } finally {
      setIsAccepting(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setCsvError('Please select a CSV file');
        return;
      }
      setCsvFile(file);
      setCsvError(null);
      setCsvAnalysis(null);
    }
  };

  const handleAnalyzeCSV = async () => {
    if (!csvFile || !selectedOrgId) return;

    setCsvLoading(true);
    setCsvError(null);

    try {
      const analysis = await analyzeDocumentCSV(csvFile, selectedOrgId);
      setCsvAnalysis(analysis);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze CSV';
      setCsvError(errorMessage);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleClearCSV = () => {
    setCsvFile(null);
    setCsvAnalysis(null);
    setCsvError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon fontSize="large" />
          Security Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Comprehensive security analysis for organization security posture monitoring
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={availableOrgs.find((org) => org.id === selectedOrgId) || null}
              onChange={(_, newValue) => setSelectedOrgId(newValue?.id || null)}
              options={availableOrgs}
              getOptionLabel={(option) => formatOrgDisplayName(option.name)}
              renderInput={(params) => <TextField {...params} label="Organization" placeholder="Select organization" />}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ToggleButtonGroup
              value={periodDays}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) setPeriodDays(newValue);
              }}
              fullWidth
              size="small"
            >
              {PERIOD_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={isLoading ? undefined : handleGenerateAssessment}
              disabled={!selectedOrgId}
              fullWidth
              startIcon={
                isLoading ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: 'white',
                    }}
                  />
                ) : undefined
              }
              sx={
                isLoading
                  ? {
                      pointerEvents: 'none',
                      opacity: 0.9,
                    }
                  : undefined
              }
            >
              {isLoading ? 'Generating...' : 'Generate Assessment'}
            </Button>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={() => setExportDialogOpen(true)}
              disabled={!assessment}
              fullWidth
              startIcon={<PictureAsPdfIcon />}
            >
              Export
            </Button>
          </Grid>

          {/* Sensitivity Categories Selection */}
          {includeSensitiveAnalysis && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Detection Categories
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[
                    { value: 'PII' as SensitivityCategory, label: 'PII', icon: '👤' },
                    { value: 'credentials' as SensitivityCategory, label: 'Credentials', icon: '🔑' },
                    { value: 'financial' as SensitivityCategory, label: 'Financial', icon: '💳' },
                    { value: 'health' as SensitivityCategory, label: 'Health', icon: '🏥' },
                    { value: 'legal' as SensitivityCategory, label: 'Legal', icon: '⚖️' },
                    { value: 'confidential' as SensitivityCategory, label: 'Confidential', icon: '🔒' },
                  ].map((category) => {
                    const isSelected = selectedSensitivityCategories.includes(category.value);
                    return (
                      <Chip
                        key={category.value}
                        icon={<span>{category.icon}</span>}
                        label={category.label}
                        onClick={() => {
                          if (isSelected) {
                            // Don't allow deselecting if it's the last one
                            if (selectedSensitivityCategories.length > 1) {
                              setSelectedSensitivityCategories(
                                selectedSensitivityCategories.filter((c) => c !== category.value)
                              );
                            }
                          } else {
                            setSelectedSensitivityCategories([...selectedSensitivityCategories, category.value]);
                          }
                        }}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* CSV Document Analysis Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon />
          Document Sensitivity Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a CSV file with document summaries to analyze for sensitive content
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="csv-file-input"
          />
          <label htmlFor="csv-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={!selectedOrgId}
            >
              Select CSV File
            </Button>
          </label>

          {csvFile && (
            <>
              <Chip
                label={csvFile.name}
                onDelete={handleClearCSV}
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={csvLoading ? undefined : handleAnalyzeCSV}
                disabled={csvLoading}
                startIcon={
                  csvLoading ? (
                    <CircularProgress
                      size={20}
                      sx={{ color: 'white' }}
                    />
                  ) : undefined
                }
                sx={
                  csvLoading
                    ? {
                        pointerEvents: 'none',
                        opacity: 0.9,
                      }
                    : undefined
                }
              >
                {csvLoading ? 'Analyzing...' : 'Analyze Documents'}
              </Button>
            </>
          )}
        </Box>

        {csvError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {csvError}
          </Alert>
        )}

        {csvAnalysis && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Total Documents
                    </Typography>
                    <Typography variant="h4">{csvAnalysis.totalDocuments}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Sensitive Documents
                    </Typography>
                    <Typography variant="h4" color="error">
                      {csvAnalysis.sensitiveDocuments}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      By Severity
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {csvAnalysis.criticalCount > 0 && (
                        <Chip
                          label={`Critical: ${csvAnalysis.criticalCount}`}
                          color="error"
                          size="small"
                        />
                      )}
                      {csvAnalysis.highCount > 0 && (
                        <Chip
                          label={`High: ${csvAnalysis.highCount}`}
                          sx={{ bgcolor: '#f97316', color: 'white' }}
                          size="small"
                        />
                      )}
                      {csvAnalysis.mediumCount > 0 && (
                        <Chip
                          label={`Medium: ${csvAnalysis.mediumCount}`}
                          color="warning"
                          size="small"
                        />
                      )}
                      {csvAnalysis.lowCount > 0 && (
                        <Chip
                          label={`Low: ${csvAnalysis.lowCount}`}
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Executive Summary */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'black' }}>
                <InfoOutlinedIcon sx={{ color: 'black' }} />
                Executive Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {csvAnalysis.sensitiveDocuments === 0 ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>No Sensitive Content Detected</AlertTitle>
                  All {csvAnalysis.totalDocuments} documents analyzed appear to be safe. No sensitive information
                  requiring immediate attention was identified.
                </Alert>
              ) : (
                <>
                  <Typography variant="body1" paragraph>
                    Analysis of {csvAnalysis.totalDocuments} document summaries identified{' '}
                    <strong>
                      {csvAnalysis.sensitiveDocuments} documents (
                      {((csvAnalysis.sensitiveDocuments / csvAnalysis.totalDocuments) * 100).toFixed(1)}%)
                    </strong>{' '}
                    containing sensitive information that may pose security or compliance risks.
                  </Typography>

                  {(csvAnalysis.criticalCount > 0 || csvAnalysis.highCount > 0) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <AlertTitle>Critical Findings</AlertTitle>
                      {csvAnalysis.criticalCount > 0 && (
                        <Typography variant="body2">
                          • <strong>{csvAnalysis.criticalCount} critical-severity documents</strong> contain highly
                          sensitive data (credentials, SSNs, financial accounts) requiring immediate review and
                          remediation.
                        </Typography>
                      )}
                      {csvAnalysis.highCount > 0 && (
                        <Typography variant="body2">
                          • <strong>{csvAnalysis.highCount} high-severity documents</strong> contain PII, health
                          records, or confidential business information that should be properly secured.
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {Object.keys(csvAnalysis.bySensitivityType).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Detected Sensitivity Types:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {Object.entries(csvAnalysis.bySensitivityType)
                          .sort(([, a], [, b]) => b - a)
                          .map(([type, count]) => (
                            <Chip
                              key={type}
                              label={`${type}: ${count}`}
                              size="small"
                              color={
                                type === 'Credentials' || type === 'PII'
                                  ? 'error'
                                  : type === 'Financial' || type === 'Health'
                                  ? 'warning'
                                  : 'default'
                              }
                              variant="outlined"
                            />
                          ))}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommended Actions:
                    </Typography>
                    <List dense>
                      {csvAnalysis.criticalCount > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Immediately review and remove critical-severity documents"
                            secondary="Documents containing credentials, SSNs, or financial account numbers should never be uploaded"
                          />
                        </ListItem>
                      )}
                      {csvAnalysis.highCount > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Audit access controls for high-severity documents"
                            secondary="Ensure only authorized personnel can access documents with PII or confidential data"
                          />
                        </ListItem>
                      )}
                      <ListItem>
                        <ListItemText
                          primary="Implement document classification policies"
                          secondary="Establish guidelines for what types of documents should be uploaded to the platform"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Conduct user training"
                          secondary="Educate users on data sensitivity and appropriate document handling practices"
                        />
                      </ListItem>
                    </List>
                  </Box>
                </>
              )}
            </Paper>

            {csvAnalysis.flaggedDocuments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Flagged Documents ({csvAnalysis.flaggedDocuments.length})
                </Typography>
                {csvAnalysis.flaggedDocuments.map((doc, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Chip
                          label={doc.sensitivityLevel}
                          size="small"
                          color={
                            doc.sensitivityLevel === 'critical'
                              ? 'error'
                              : doc.sensitivityLevel === 'high'
                              ? 'warning'
                              : doc.sensitivityLevel === 'medium'
                              ? 'info'
                              : 'default'
                          }
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {doc.documentSummary.substring(0, 100)}...
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Document Summary:
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {doc.documentSummary}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          Detected Types:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {doc.detectedTypes.map((type) => (
                            <Chip key={type} label={type} size="small" variant="outlined" />
                          ))}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          Risk Explanation:
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {doc.riskExplanation}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          Recommended Action:
                        </Typography>
                        <Typography variant="body2">
                          {doc.recommendedAction}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box>
          {/* Overall Score Skeleton */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Skeleton variant="circular" width={120} height={120} />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={30} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                  <Skeleton variant="rectangular" width={100} height={60} />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Category Scores Skeleton */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={300} />
          </Paper>

          {/* Executive Summary Skeleton */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
            </Box>
          </Paper>

          {/* Risks Skeleton */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 2, pb: 2, borderBottom: i < 3 ? '1px solid #e0e0e0' : 'none' }}>
                <Skeleton variant="text" width="50%" height={28} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={20} />
              </Box>
            ))}
          </Paper>

          {/* Metrics Grid Skeleton */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Paper sx={{ p: 3 }}>
                  <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="75%" height={20} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Assessment Results */}
      {assessment && !isLoading && (
        <Box>
          {/* Overall Score Card */}
          <OverallScoreCard assessment={assessment} />

          {/* Category Scores */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Security Categories
            </Typography>
            <CategoryScoresChart categoryScores={assessment.categoryScores} />
          </Paper>

          {/* Executive Summary */}
          <ExecutiveSummaryCard summary={assessment.executiveSummary} healthStatus={assessment.healthStatus} />

          {/* Risks */}
          <RisksCard
            risks={assessment.risks}
            complianceRisks={assessment.complianceRisks}
            onAcceptRisk={handleOpenAcceptDialog}
          />

          {/* Sensitive Content Analysis */}
          <SensitiveContentCard sensitiveRequests={assessment.sensitiveRequests} />

          {/* Security Metrics */}
          <SecurityMetricsCard metrics={assessment.metrics} />

          {/* Remediation Plan */}
          <RemediationPlanCard remediationPlan={assessment.remediationPlan} />
        </Box>
      )}

      {/* Empty State */}
      {!assessment && !isLoading && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Assessment Generated
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select an organization and click "Generate Assessment" to begin security analysis
          </Typography>
        </Paper>
      )}

    <GenerateSecurityReportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        currentAssessment={assessment}
        csvAnalysis={csvAnalysis}
      />

      {/* Accept Risk Confirmation Dialog */}
      <Dialog open={acceptRiskDialogOpen} onClose={() => setAcceptRiskDialogOpen(false)}>
        <DialogTitle>Dismiss Security Risk</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to dismiss this risk? It will be removed from the report and score calculation.
          </DialogContentText>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {selectedRiskToAccept?.title}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for dismissal (optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={acceptReason}
            onChange={(e) => setAcceptReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptRiskDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAcceptRisk} variant="contained" color="primary" disabled={isAccepting}>
            {isAccepting ? 'Dismissing...' : 'Dismiss Risk'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Overall Score Card Component
function OverallScoreCard({ assessment }: { assessment: any }) {
  const config = HEALTH_STATUS_CONFIG[assessment.healthStatus as HealthStatus];
  const scorePercentage = assessment.overallScore;

  return (
    <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${config.color}15 0%, transparent 100%)` }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
          {config.icon}
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: config.color }}>
            {scorePercentage.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            / 100
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h5" gutterBottom>
            {config.label} Security Posture
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {config.description}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={scorePercentage}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                backgroundColor: config.color,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Critical Risks:</Typography>
              <Chip label={assessment.criticalRiskCount} size="small" color="error" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">High Risks:</Typography>
              <Chip label={assessment.highRiskCount} size="small" sx={{ bgcolor: '#f97316', color: 'white' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Medium Risks:</Typography>
              <Chip label={assessment.mediumRiskCount} size="small" color="warning" />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Low Risks:</Typography>
              <Chip label={assessment.lowRiskCount} size="small" color="info" />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

// Category Scores Chart Component
function CategoryScoresChart({ categoryScores }: { categoryScores: CategoryScore[] }) {
  const chartData = categoryScores.map((score) => ({
    name: score.categoryName,
    score: Number(score.score.toFixed(1)),
    maxScore: Number(score.maxScore.toFixed(1)),
    percentage: Number(((score.score / score.maxScore) * 100).toFixed(1)),
    weight: score.weight,
  }));

  return (
    <Box>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} />
          <YAxis type="category" dataKey="name" width={150} />
          <Tooltip
            formatter={(value, name) => {
              if (name === 'percentage') return [`${Number(value).toFixed(1)}%`, 'Score'];
              return [value, name];
            }}
          />
          <Bar dataKey="percentage" fill="#3b82f6" radius={[0, 8, 8, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.percentage >= 90 ? '#10b981' : entry.percentage >= 70 ? '#f59e0b' : '#ef4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Category Details */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {categoryScores.map((score) => {
          const percentage = ((score.score / score.maxScore) * 100).toFixed(1);
          return (
            <Grid item xs={12} md={6} key={score.category}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="subtitle2">{score.categoryName}</Typography>
                    <MuiTooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="body2">{CATEGORY_DESCRIPTIONS[score.categoryName] || ''}</Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">{percentage}%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 100%
                    </Typography>
                  </Box>
                  {score.issuesFound.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="error" display="block" gutterBottom>
                        Issues:
                      </Typography>
                      {score.issuesFound.map((issue, idx) => (
                        <Typography key={idx} variant="caption" color="text.secondary" display="block">
                          • {issue}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

// Executive Summary Card Component
function ExecutiveSummaryCard({ summary }: { summary: any; healthStatus?: HealthStatus }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Executive Summary
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
        {summary.narrative}
      </Typography>

      <Grid container spacing={3}>
        {summary.keyStrengths.length > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <AlertTitle>Key Strengths</AlertTitle>
              <List dense>
                {summary.keyStrengths.map((strength: string, idx: number) => (
                  <ListItem key={idx} sx={{ py: 0 }}>
                    <ListItemText primary={strength} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Grid>
        )}

        {(summary.criticalConcerns.length > 0 || summary.highConcerns.length > 0) && (
          <Grid item xs={12} md={6}>
            <Alert severity="error" icon={<ErrorIcon />}>
              <AlertTitle>Critical & High Concerns</AlertTitle>
              <List dense>
                {summary.criticalConcerns.map((concern: string, idx: number) => (
                  <ListItem key={`critical-${idx}`} sx={{ py: 0 }}>
                    <ListItemText primary={`[Critical] ${concern}`} />
                  </ListItem>
                ))}
                {summary.highConcerns.map((concern: string, idx: number) => (
                  <ListItem key={`high-${idx}`} sx={{ py: 0 }}>
                    <ListItemText primary={`[High] ${concern}`} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Grid>
        )}

        {summary.weakCategories.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>Areas Needing Improvement</AlertTitle>
              <Typography variant="body2">{summary.weakCategories.join(', ')}</Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

// Risks Card Component
function RisksCard({
  risks,
  complianceRisks,
  onAcceptRisk,
}: {
  risks: SecurityRisk[];
  complianceRisks?: Record<string, SecurityRisk[]>;
  onAcceptRisk?: (risk: SecurityRisk) => void;
}) {
  const [viewMode, setViewMode] = useState<'security' | 'compliance'>('security');

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Identified Risks ({risks.length})
        </Typography>
        {complianceRisks && Object.keys(complianceRisks).length > 0 && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="security">Security View</ToggleButton>
            <ToggleButton value="compliance">Compliance View</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {risks.length === 0 ? (
        <Alert severity="success">
          <AlertTitle>No Risks Detected</AlertTitle>
          No significant security risks were identified during this assessment period.
        </Alert>
      ) : (
        <Box>
          {viewMode === 'security' ? (
            // Security Category View
            risks.map((risk) => (
              <RiskItem key={risk.riskId} risk={risk} onAcceptRisk={onAcceptRisk} />
            ))
          ) : (
            // Compliance Framework View
            <Box>
              {complianceRisks &&
                Object.entries(complianceRisks).map(([framework, frameworkRisks]) => (
                  <Box key={framework} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
                      {framework} Framework
                    </Typography>
                    {frameworkRisks.map((risk) => (
                      <RiskItem key={`${framework}-${risk.riskId}`} risk={risk} onAcceptRisk={onAcceptRisk} showCompliance={framework} />
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

function RiskItem({
  risk,
  onAcceptRisk,
  showCompliance,
}: {
  risk: SecurityRisk;
  onAcceptRisk?: (risk: SecurityRisk) => void;
  showCompliance?: string;
}) {
  const severityConfig = RISK_SEVERITY_CONFIG[risk.severity];

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
          <Chip
            label={severityConfig.label}
            size="small"
            sx={{ bgcolor: severityConfig.color, color: 'white', minWidth: 80 }}
          />
          <Typography variant="subtitle1" sx={{ flex: 1 }}>
            {risk.title}
          </Typography>
          {showCompliance ? (
            // Show specific compliance controls if in compliance view
            <Chip
              label={risk.complianceMappings?.[showCompliance]?.join(', ') || 'Mapped'}
              size="small"
              variant="outlined"
              color="primary"
            />
          ) : (
            <Chip label={`Priority ${risk.priority}`} size="small" variant="outlined" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {risk.description}
          </Typography>

          {/* Compliance Mappings Info (always show in detail) */}
          {risk.complianceMappings && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(risk.complianceMappings).map(([fw, controls]) => (
                <MuiTooltip key={fw} title={controls.join(', ')}>
                  <Chip label={`${fw}: ${controls.length} controls`} size="small" color="info" variant="outlined" />
                </MuiTooltip>
              ))}
            </Box>
          )}

          {risk.affectedResources.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Affected Resources ({risk.affectedCount}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {risk.affectedResources.slice(0, 10).map((resource, idx) => (
                  <Chip key={idx} label={resource} size="small" variant="outlined" />
                ))}
                {risk.affectedResources.length > 10 && (
                  <Chip
                    label={`+${risk.affectedResources.length - 10} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}

          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
            Remediation Steps:
          </Typography>
          <List dense sx={{ mb: 2 }}>
            {risk.remediationSteps.map((step, idx) => (
              <ListItem key={idx} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${idx + 1}. ${step}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          {onAcceptRisk && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                size="small"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  onAcceptRisk(risk);
                }}
                startIcon={<CheckCircleIcon fontSize="small" />}
              >
                Dismiss Risk
              </Button>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

// Sensitive Content Analysis Card Component
function SensitiveContentCard({ sensitiveRequests }: { sensitiveRequests: SensitivityBreakdown }) {
  const hasData = sensitiveRequests.totalRequestsAnalyzed > 0;
  const hasSensitiveContent = sensitiveRequests.totalSensitiveRequests > 0;

  const getSeverityColor = (severity: string): string => {
    const colors: Record<string, string> = {
      critical: '#dc2626',
      high: '#f97316',
      medium: '#fbbf24',
      low: '#10b981',
    };
    return colors[severity] || '#6b7280';
  };

  const getSensitivityIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pii':
        return '👤';
      case 'credentials':
        return '🔑';
      case 'financial':
        return '💳';
      case 'health':
        return '🏥';
      case 'legal':
        return '⚖️';
      case 'confidential':
        return '🔒';
      default:
        return '📄';
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        AI-Powered Sensitive Content Analysis
      </Typography>

      {!hasData ? (
        <Alert severity="info">
          <AlertTitle>No Analysis Available</AlertTitle>
          No AI requests were found in the analysis period, or sensitive content analysis was disabled.
        </Alert>
      ) : !hasSensitiveContent ? (
        <Alert severity="success">
          <AlertTitle>No Sensitive Content Detected</AlertTitle>
          Analyzed {sensitiveRequests.totalRequestsAnalyzed} requests - no sensitive content was detected.
        </Alert>
      ) : (
        <Box>
          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Analyzed
              </Typography>
              <Typography variant="h4">{sensitiveRequests.totalRequestsAnalyzed}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Sensitive Detected
              </Typography>
              <Typography variant="h4" color="error">
                {sensitiveRequests.totalSensitiveRequests}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({((sensitiveRequests.totalSensitiveRequests / sensitiveRequests.totalRequestsAnalyzed) * 100).toFixed(1)}%)
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Affected Users
              </Typography>
              <Typography variant="h4">{sensitiveRequests.affectedUsers}</Typography>
            </Box>
          </Box>

          {/* Breakdown by Severity */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              By Severity
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(sensitiveRequests.bySeverity).map(([severity, count]) => {
                if (count === 0) return null;
                return (
                  <Chip
                    key={severity}
                    label={`${severity.charAt(0).toUpperCase() + severity.slice(1)}: ${count}`}
                    size="small"
                    sx={{
                      bgcolor: getSeverityColor(severity),
                      color: 'white',
                      fontWeight: 'medium',
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Breakdown by Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              By Content Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(sensitiveRequests.bySensitivityType).map(([type, count]) => (
                <Chip
                  key={type}
                  icon={<span>{getSensitivityIcon(type)}</span>}
                  label={`${type}: ${count}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {/* Breakdown by Request Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              By Request Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(sensitiveRequests.byRequestType).map(([type, count]) => (
                <Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" color="primary" />
              ))}
            </Box>
          </Box>

          {/* Examples */}
          {sensitiveRequests.examples.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Top Examples
              </Typography>
              {sensitiveRequests.examples.map((example) => {
                const severityConfig = RISK_SEVERITY_CONFIG[example.sensitivityLevel];
                return (
                  <Accordion key={example.traceId} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Chip label={severityConfig.label} size="small" sx={{ bgcolor: severityConfig.color, color: 'white' }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {example.requestType} - {example.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(example.timestamp).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                            Detected Content Types:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {example.detectedTypes.map((type) => (
                              <Chip
                                key={type}
                                icon={<span>{getSensitivityIcon(type)}</span>}
                                label={type}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                            Explanation:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {example.explanation}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                            Request Preview:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {example.requestPreview}
                          </Typography>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Trace ID: {example.traceId} | User: {example.userName}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

// Security Metrics Card Component
function SecurityMetricsCard({ metrics }: { metrics: any }) {
  const metricGroups = [
    {
      title: 'Authentication',
      metrics: [
        { label: 'Failed Login Attempts', value: metrics.failedLoginAttempts },
        { label: 'Off-Hours Logins', value: metrics.offHoursLogins },
        { label: 'Unusual Locations', value: metrics.unusualLocations },
      ],
    },
    {
      title: 'Access Control',
      metrics: [
        { label: 'Privileged Users', value: metrics.privilegedUsers },
        { label: 'Permission Escalations', value: metrics.permissionEscalations },
      ],
    },
    {
      title: 'Data Security',
      metrics: [
        { label: 'Medium Bulk Operations (10-49 docs)', value: metrics.bulkOperationsMedium },
        { label: 'Large Bulk Operations (50+ docs)', value: metrics.bulkOperationsLarge },
        { label: 'External Shares', value: metrics.externalShares },
      ],
    },
    {
      title: 'User Behavior',
      metrics: [
        { label: 'High-Access Users (500+ events)', value: metrics.highAccessUsers },
        { label: 'Suspicious Activity Patterns', value: metrics.suspiciousActivityCount },
      ],
    },
  ];

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Security Metrics
      </Typography>
      <Grid container spacing={3}>
        {metricGroups.map((group) => (
          <Grid item xs={12} md={6} key={group.title}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  {group.title}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {group.metrics.map((metric, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {metric.label}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

// Remediation Plan Card Component
function RemediationPlanCard({ remediationPlan }: { remediationPlan: RemediationAction[] }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Remediation Action Plan
      </Typography>

      {remediationPlan.length === 0 ? (
        <Alert severity="info">
          <AlertTitle>No Actions Required</AlertTitle>
          No remediation actions are needed at this time.
        </Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Risks</TableCell>
                <TableCell>Effort</TableCell>
                <TableCell>Time Estimate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {remediationPlan.map((action, _idx) => (
                <TableRow key={action.category}>
                  <TableCell>
                    <Chip label={`#${action.priorityOrder}`} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{action.categoryName}</TableCell>
                  <TableCell>
                    <Chip label={`${action.riskCount} risk(s)`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={action.effortLevel}
                      size="small"
                      color={action.effortLevel === 'High' ? 'error' : action.effortLevel === 'Medium' ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell>{action.estimatedTime}</TableCell>
                  <TableCell>
                    <List dense>
                      {action.actions.slice(0, 3).map((actionItem, actionIdx) => (
                        <ListItem key={actionIdx} sx={{ py: 0 }}>
                          <ListItemText primary={`• ${actionItem}`} primaryTypographyProps={{ variant: 'caption' }} />
                        </ListItem>
                      ))}
                      {action.actions.length > 3 && (
                        <ListItem sx={{ py: 0 }}>
                          <ListItemText
                            primary={`...and ${action.actions.length - 3} more`}
                            primaryTypographyProps={{ variant: 'caption', fontStyle: 'italic' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
